import { Link } from 'react-router';
import type { FunctionComponent } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router';

import { NEW_TAB_PROPS } from './Link';
import type { LinkProps } from './Link';

type Props = Omit<RouterLinkProps, 'children'> & LinkProps;

/**
 * Wrapper around react-router's link component.
 *
 * @param {Props} props - RouterLink props
 * @param {React.ReactNode} props.children - Text/content of the link.
 * @param {boolean} props.openInNewTab - If the link should open in a new tab.
 * @param {string} props.to - Where to link to.
 * @param {string} [props.className] - Optional className.
 * @param {React.CSSProperties} [props.style] - Optional styles.
 * @param {string} [props.testId] - Adds a `data-test-id` attribute, useful for selenium testing.
 */
const RouterLink: FunctionComponent<Props> = ({
  children,
  openInNewTab,
  testId,
  ...linkProps
}) => (
  <Link
    //  react-router doesn't support open in new tab, but our link component passes openInNewTab anyway. This fixes that.
    //  We often use this pattern for `/api/` download URLs.
    {...(openInNewTab ? NEW_TAB_PROPS : {})}
    {...linkProps}
    data-test-id={testId}
  >
    {
      //  Note that react-router incorrectly types `children`, so we override it.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children as any
    }
  </Link>
);

export default RouterLink;
